import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDataProvider, useNotify } from 'react-admin';
import { DEFAULT_ADS_REASONS, DEFAULT_EXPERT_REASONS, DEFAULT_REVIEW_REASONS } from './reasonsConfig';

type CustomReason = {
    id: string;
    label: string;
    value: string;
    group: 'ads' | 'expert' | 'reviews';
    reasonType: 'refusal' | 'rework';
    createdAt?: string;
    updatedAt?: string;
};

export const ReasonsPage = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [customReasons, setCustomReasons] = useState<CustomReason[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingReason, setEditingReason] = useState<CustomReason | null>(null);
    const [deletingReason, setDeletingReason] = useState<CustomReason | null>(null);
    const [activeGroup, setActiveGroup] = useState<CustomReason['group']>('ads');
    const [reasonType, setReasonType] = useState<CustomReason['reasonType']>('rework');
    const [label, setLabel] = useState('');
    const [value, setValue] = useState('');

    const resetDialog = useCallback(() => {
        setEditingReason(null);
        setActiveGroup('ads');
        setReasonType('rework');
        setLabel('');
        setValue('');
    }, []);

    const loadReasons = useCallback(async () => {
        setLoading(true);
        try {
            const result = await dataProvider.getList('customReasons', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: {}
            });
            const loaded = ((result?.data as CustomReason[]) || []).map((reason) => ({
                ...reason,
                group: reason.group ?? 'ads',
                reasonType: reason.reasonType ?? (reason.group === 'expert' || reason.group === 'reviews' ? 'refusal' : 'rework')
            }));
            setCustomReasons(loaded);
        } catch (error) {
            console.error('Failed to load custom reasons', error);
            notify('Не удалось загрузить пользовательские причины', { type: 'warning' });
        } finally {
            setLoading(false);
        }
    }, [dataProvider, notify]);

    useEffect(() => {
        loadReasons();
    }, [loadReasons]);

    const handleOpenCreateForGroup = (group: CustomReason['group']) => {
        resetDialog();
        setActiveGroup(group);
        setReasonType(group === 'expert' || group === 'reviews' ? 'refusal' : 'rework');
        setDialogOpen(true);
    };

    const handleOpenEdit = (reason: CustomReason) => {
        setEditingReason(reason);
        setActiveGroup(reason.group);
        setReasonType(reason.reasonType ?? (reason.group === 'expert' || reason.group === 'reviews' ? 'refusal' : 'rework'));
        setLabel(reason.label);
        setValue(reason.value);
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        resetDialog();
    };

    const handleOpenDelete = (reason: CustomReason) => {
        setDeletingReason(reason);
        setDeleteDialogOpen(true);
    };

    const handleCloseDelete = () => {
        setDeleteDialogOpen(false);
        setDeletingReason(null);
    };

    const handleSave = async () => {
        const cleanedLabel = label.trim();
        const cleanedValue = value.trim();

        if (!cleanedLabel || !cleanedValue) {
            notify('Заполните заголовок и текст причины', { type: 'warning' });
            return;
        }

        try {
            const resolvedReasonType = activeGroup === 'expert' || activeGroup === 'reviews' ? 'refusal' : reasonType;
            if (editingReason) {
                await dataProvider.update('customReasons', {
                    id: editingReason.id,
                    data: { label: cleanedLabel, value: cleanedValue, group: editingReason.group, reasonType: resolvedReasonType },
                    previousData: editingReason
                });
                notify('Причина обновлена', { type: 'info' });
            } else {
                await dataProvider.create('customReasons', {
                    data: { label: cleanedLabel, value: cleanedValue, group: activeGroup, reasonType: resolvedReasonType }
                });
                notify('Причина добавлена', { type: 'info' });
            }
            handleClose();
            loadReasons();
        } catch (error) {
            console.error('Failed to save custom reason', error);
            notify('Не удалось сохранить причину', { type: 'warning' });
        }
    };

    const handleDelete = async (reason: CustomReason) => {
        try {
            await dataProvider.delete('customReasons', { id: reason.id, previousData: reason });
            notify('Причина удалена', { type: 'info' });
            loadReasons();
        } catch (error) {
            console.error('Failed to delete custom reason', error);
            notify('Не удалось удалить причину', { type: 'warning' });
        } finally {
            handleCloseDelete();
        }
    };

    const dialogTitle = useMemo(() => (editingReason ? 'Редактировать причину' : 'Новая причина'), [editingReason]);
    const customAdsReasons = useMemo(
        () => customReasons.filter((reason) => reason.group === 'ads'),
        [customReasons]
    );
    const customExpertReasons = useMemo(
        () => customReasons.filter((reason) => reason.group === 'expert'),
        [customReasons]
    );
    const customReviewReasons = useMemo(
        () => customReasons.filter((reason) => reason.group === 'reviews'),
        [customReasons]
    );
    const reasonTypeLabel = (type: CustomReason['reasonType']) =>
        type === 'refusal' ? 'Для отказа' : 'Для доработки';

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Причины отказа
            </Typography>

            <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Причины: модерация объявлений</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Базовые причины (только чтение)
                    </Typography>
                    <Stack spacing={2} sx={{ mb: 3 }}>
                        {DEFAULT_ADS_REASONS.map((reason) => (
                            <Box key={reason.label}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="subtitle1">{reason.label}</Typography>
                                    <Chip label={reasonTypeLabel(reason.reasonType)} size="small" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {reason.value}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1">Пользовательские причины</Typography>
                            <IconButton onClick={() => handleOpenCreateForGroup('ads')} size="small">
                                <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    {loading ? (
                        <CircularProgress size={28} />
                    ) : customAdsReasons.length === 0 ? (
                        <Alert severity="info">Пока нет пользовательских причин.</Alert>
                    ) : (
                        <Stack spacing={2}>
                            {customAdsReasons.map((reason) => (
                                <Box key={reason.id}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                        <Typography variant="subtitle1">{reason.label}</Typography>
                                        <Chip label={reasonTypeLabel(reason.reasonType)} size="small" />
                                        <IconButton onClick={() => handleOpenEdit(reason)} size="small">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleOpenDelete(reason)} size="small" color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        {reason.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Причины: экспертная модерация</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Базовые причины (только чтение)
                    </Typography>
                    <Stack spacing={2} sx={{ mb: 3 }}>
                        {DEFAULT_EXPERT_REASONS.map((reason) => (
                            <Box key={reason.label}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="subtitle1">{reason.label}</Typography>
                                    <Chip label={reasonTypeLabel(reason.reasonType)} size="small" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {reason.value}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1">Пользовательские причины</Typography>
                            <IconButton onClick={() => handleOpenCreateForGroup('expert')} size="small">
                                <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    {loading ? (
                        <CircularProgress size={28} />
                    ) : customExpertReasons.length === 0 ? (
                        <Alert severity="info">Пока нет пользовательских причин.</Alert>
                    ) : (
                        <Stack spacing={2}>
                            {customExpertReasons.map((reason) => (
                                <Box key={reason.id}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                        <Typography variant="subtitle1">{reason.label}</Typography>
                                        <Chip label={reasonTypeLabel(reason.reasonType)} size="small" />
                                        <IconButton onClick={() => handleOpenEdit(reason)} size="small">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleOpenDelete(reason)} size="small" color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        {reason.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Причины: модерация отзывов</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Базовые причины (только чтение)
                    </Typography>
                    <Stack spacing={2} sx={{ mb: 3 }}>
                        {DEFAULT_REVIEW_REASONS.map((reason) => (
                            <Box key={reason.label}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="subtitle1">{reason.label}</Typography>
                                    <Chip label={reasonTypeLabel(reason.reasonType)} size="small" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {reason.value}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1">Пользовательские причины</Typography>
                            <IconButton onClick={() => handleOpenCreateForGroup('reviews')} size="small">
                                <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    {loading ? (
                        <CircularProgress size={28} />
                    ) : customReviewReasons.length === 0 ? (
                        <Alert severity="info">Пока нет пользовательских причин.</Alert>
                    ) : (
                        <Stack spacing={2}>
                            {customReviewReasons.map((reason) => (
                                <Box key={reason.id}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                        <Typography variant="subtitle1">{reason.label}</Typography>
                                        <Chip label={reasonTypeLabel(reason.reasonType)} size="small" />
                                        <IconButton onClick={() => handleOpenEdit(reason)} size="small">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleOpenDelete(reason)} size="small" color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        {reason.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </AccordionDetails>
            </Accordion>

            <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Заголовок"
                            value={label}
                            onChange={(event) => setLabel(event.target.value)}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="reason-type-label">Тип причины</InputLabel>
                            <Select
                                labelId="reason-type-label"
                                label="Тип причины"
                                value={reasonType}
                                disabled={activeGroup === 'expert' || activeGroup === 'reviews'}
                                onChange={(event) => setReasonType(event.target.value as CustomReason['reasonType'])}
                            >
                                <MenuItem value="refusal">Для отказа</MenuItem>
                                <MenuItem value="rework">Для доработки</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Текст причины"
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            helperText="Этот текст увидит пользователь"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Отмена</Button>
                    <Button onClick={handleSave} variant="contained">
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={handleCloseDelete} fullWidth maxWidth="xs">
                <DialogTitle>Удалить причину?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {deletingReason ? `Вы уверены, что хотите удалить причину "${deletingReason.label}"?` : 'Вы уверены, что хотите удалить эту причину?'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete}>Отмена</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => deletingReason && handleDelete(deletingReason)}
                    >
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
